import { useTranslation } from "react-i18next";
import type { FaqEntry } from "@/lib/faqResolver";

/**
 * Render a route's FAQ entries as visible question-and-answer prose.
 *
 * Injected on every indexed route by {@link LanguageRouter}, so this component
 * decides whether FAQ answer text exists in the DOM of all 250 route × locale
 * pages. Answers are body prose under `<h3>` question headings rather than
 * collapsible panels: Radix unmounts closed accordion content, which left the
 * answers absent from the DOM entirely, and `forceMount` yields an open panel
 * rather than a collapsed one. Full reasoning in
 * `.system_design/SYSTEM_DESIGN.md` §16.1 (CWS-56, plan step E4.2, SEO.md B6).
 *
 * @param faqs - The resolved FAQ entries for the current route, in the order
 *   the matching `FAQPage` JSON-LD uses.
 * @returns The FAQ section element, or `null` when there are no entries.
 */
export function VisibleFaqSection({ faqs }: { faqs: FaqEntry[] }) {
  const { t } = useTranslation();

  if (!faqs.length) {
    return null;
  }

  return (
    <section className="border-t border-border/50 bg-muted/20 py-16">
      <div className="container max-w-4xl">
        <h2 className="text-3xl font-bold tracking-tight text-foreground">
          {t("common.faqTitle", { defaultValue: "Frequently Asked Questions" })}
        </h2>
        <p className="mt-3 text-muted-foreground">
          {t("common.faqSubtitle", { defaultValue: "Answers to common questions about this topic." })}
        </p>
        <div className="mt-8 rounded-lg border border-border/60 bg-background px-6">
          {faqs.map((faq, index) => (
            // One container per pair, so the block parses as discrete Q/A units.
            <div
              key={`${faq.question}-${index}`}
              className={index === faqs.length - 1 ? "py-5" : "border-b py-5"}
            >
              <h3 className="text-base font-medium text-foreground">{faq.question}</h3>
              <p className="mt-2 text-base text-muted-foreground">{faq.answer}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
